import React, { useState, useEffect } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";

const emptyForm = { asset_head_id: "", name: "", detail: "" };

const AssetItemMasterList = () => {
  const { http } = AuthUser();
  const [rows, setRows] = useState([]);
  const [heads, setHeads] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [selectedHead, setSelectedHead] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchRows();
    fetchHeads();
  }, []);

  const fetchRows = async () => {
    try {
      const response = await http.get("/asset_item_masters");
      if (response.data.success) setRows(response.data.data);
    } catch (error) {
      toast.error("Error loading item masters");
    }
  };

  const fetchHeads = async () => {
    try {
      const response = await http.get("/asset_heads");
      if (response.data.success) setHeads(response.data.data || []);
    } catch (error) {
      toast.error("Error loading asset heads");
    }
  };

  const headOptions = heads.map((h) => ({
    value: h.id,
    label: h.name,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.asset_head_id) {
      toast.error("Please select a head");
      return;
    }
    if (!formData.name?.trim()) {
      toast.error("Item master name is required");
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        asset_head_id: formData.asset_head_id,
        name: formData.name,
        detail: formData.detail || "",
      };
      if (editing) {
        await http.put(`/asset_item_masters/${editing.id}`, payload);
        toast.success("Item master updated successfully");
      } else {
        await http.post("/asset_item_masters", payload);
        toast.success("Item master created successfully");
      }
      setShowModal(false);
      setFormData(emptyForm);
      setSelectedHead(null);
      setEditing(null);
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving item master");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({
      asset_head_id: row.asset_head_id || "",
      name: row.name || "",
      detail: row.detail || "",
    });
    setSelectedHead(
      row.head
        ? { value: row.head.id, label: row.head.name }
        : headOptions.find((o) => o.value === row.asset_head_id) || null
    );
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this item master?")) return;
    try {
      await http.delete(`/asset_item_masters/${id}`);
      toast.success("Item master deleted");
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error deleting");
    }
  };

  const filtered = rows.filter((r) => {
    const q = searchTerm.toLowerCase();
    return (
      (r.name || "").toLowerCase().includes(q) ||
      (r.detail || "").toLowerCase().includes(q) ||
      (r.head?.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="container-fluid mt-4">
      <Toaster />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Asset Item Master</h5>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => {
              setEditing(null);
              setFormData(emptyForm);
              setSelectedHead(null);
              setShowModal(true);
            }}
          >
            <i className="fas fa-plus-circle me-1"></i> Add Item Master
          </button>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, detail or head..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Head</th>
                  <th>Item Master Name</th>
                  <th>Detail</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((row) => (
                    <tr key={row.id}>
                      <td>{row.id}</td>
                      <td>{row.head?.name || "-"}</td>
                      <td>{row.name}</td>
                      <td>{row.detail || "-"}</td>
                      <td>
                        {row.created_at
                          ? new Date(row.created_at).toLocaleDateString()
                          : "-"}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="btn btn-sm btn-warning me-1"
                          onClick={() => handleEdit(row)}
                          title="Edit"
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDelete(row.id)}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No item masters found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editing ? "Edit Item Master" : "Add Item Master"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      Asset Head <span className="text-danger">*</span>
                    </label>
                    <Select
                      options={headOptions}
                      value={selectedHead}
                      onChange={(opt) => {
                        setSelectedHead(opt);
                        setFormData({
                          ...formData,
                          asset_head_id: opt ? opt.value : "",
                        });
                      }}
                      placeholder="Search / select head..."
                      isClearable
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      Item Master Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Detail</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.detail}
                      onChange={(e) =>
                        setFormData({ ...formData, detail: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetItemMasterList;
